import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'
const cream = '#FAF7F2'

// Tag type options — bitmask must match backend seed_tags_for_user function
const TAG_OPTIONS = [
  { mask: 1, icon: '💼', titleKey: 'onboarding.tag_schedule_c_title', descKey: 'onboarding.tag_schedule_c_desc' },
  { mask: 2, icon: '🏢', titleKey: 'onboarding.tag_company_card_title', descKey: 'onboarding.tag_company_card_desc' },
  { mask: 4, icon: '💵', titleKey: 'onboarding.tag_reimbursable_title', descKey: 'onboarding.tag_reimbursable_desc' },
  { mask: 8, icon: '🏠', titleKey: 'onboarding.tag_personal_title', descKey: 'onboarding.tag_personal_desc' },
]

export default function Onboarding({ onComplete, initialStep = 0 }) {
  const { t, i18n } = useTranslation()

  const [step, setStep] = useState(initialStep)
  const [language, setLanguage] = useState(i18n.language || 'en')
  const [selectedMask, setSelectedMask] = useState(0)  // bitmask of selected options
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Step 1: Language → set + advance
  const pickLanguage = async (code) => {
    setLanguage(code)
    localStorage.setItem('i18nextLng', code)
    await i18n.changeLanguage(code)
    setStep(1)
  }

  // Step 2: Toggle a tag-type option
  const toggleTag = (mask) => {
    setSelectedMask(prev => (prev & mask) ? prev & ~mask : prev | mask)
  }

  // Step 2 → 3: save tags
  const saveTagsAndAdvance = async () => {
    if (selectedMask === 0) {
      setError(t('onboarding.pick_at_least_one'))
      return
    }

    setSaving(true)
    setError('')

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not signed in')

      // Seed the selected tag types. RPC handles idempotency.
      const { error: seedErr } = await supabase.rpc('seed_tags_for_user', {
        p_seeds_mask: selectedMask,
      })
      if (seedErr) throw seedErr

      // Persist language + mask on profile (step 2 done)
      const { error: profErr } = await supabase
        .from('profiles')
        .update({
          preferred_language: language,
          selected_tag_types_mask: selectedMask,
          onboarding_step: 2,
        })
        .eq('id', user.id)
      if (profErr) throw profErr

      setStep(2)
    } catch (err) {
      console.error('Save tags failed:', err)
      setError(err.message || t('onboarding.save_failed'))
    } finally {
      setSaving(false)
    }
  }

  // Step 3 → done: mark onboarding_completed = true and let parent route to dashboard
  const finish = async () => {
    setSaving(true)
    setError('')
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not signed in')

      const { error: err } = await supabase
        .from('profiles')
        .update({
          onboarding_completed: true,
          onboarding_step: 99,
        })
        .eq('id', user.id)
      if (err) throw err

      onComplete()
    } catch (err) {
      console.error('Finish onboarding failed:', err)
      setError(err.message || t('onboarding.save_failed'))
      setSaving(false)
    }
  }

  return (
    <div style={styles.root}>
      <div style={styles.container}>
        {/* Logo bar */}
        <div style={styles.logoBar}>
          <span style={styles.logoText}>
            Merq<span style={styles.logoLog}>Log</span>
          </span>
        </div>

        {/* Progress dots */}
        <div style={styles.dots}>
          {[0, 1, 2].map(i => (
            <div
              key={i}
              style={{
                ...styles.dot,
                background: i <= step ? teal : '#E5E7EB',
                width: i === step ? '28px' : '8px',
              }}
            />
          ))}
        </div>

        {/* Step content */}
        {step === 0 && <LanguageStep onPick={pickLanguage} t={t} />}
        {step === 1 && (
          <TagTypesStep
            t={t}
            selectedMask={selectedMask}
            onToggle={toggleTag}
            onContinue={saveTagsAndAdvance}
            onBack={() => setStep(0)}
            saving={saving}
            error={error}
          />
        )}
        {step === 2 && (
          <WelcomeStep
            t={t}
            onFinish={finish}
            saving={saving}
            error={error}
          />
        )}
      </div>
    </div>
  )
}

// ---------- Step 1: Language picker ----------
function LanguageStep({ onPick, t }) {
  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸', sub: 'Continue in English' },
    { code: 'es', label: 'Español', flag: '🇲🇽', sub: 'Continuar en Español' },
  ]

  return (
    <div style={styles.step}>
      <div style={styles.stepIcon}>🌎</div>
      <h1 style={styles.h1}>{t('onboarding.lang_title')}</h1>
      <p style={styles.sub}>{t('onboarding.lang_sub')}</p>

      <div style={styles.langGrid}>
        {languages.map(lang => (
          <button
            key={lang.code}
            style={styles.langBtn}
            onClick={() => onPick(lang.code)}
          >
            <span style={styles.langFlag}>{lang.flag}</span>
            <div style={styles.langTextCol}>
              <span style={styles.langLabel}>{lang.label}</span>
              <span style={styles.langSub}>{lang.sub}</span>
            </div>
            <span style={styles.langChevron}>→</span>
          </button>
        ))}
      </div>

      <p style={styles.footnote}>{t('onboarding.lang_footnote')}</p>
    </div>
  )
}

// ---------- Step 2: Tag types (how do you use money) ----------
function TagTypesStep({ t, selectedMask, onToggle, onContinue, onBack, saving, error }) {
  return (
    <div style={styles.step}>
      <div style={styles.stepIcon}>💸</div>
      <h1 style={styles.h1}>{t('onboarding.tags_title')}</h1>
      <p style={styles.sub}>{t('onboarding.tags_sub')}</p>

      <div style={styles.tagGrid}>
        {TAG_OPTIONS.map(opt => {
          const selected = !!(selectedMask & opt.mask)
          return (
            <button
              key={opt.mask}
              type="button"
              onClick={() => onToggle(opt.mask)}
              style={{
                ...styles.tagCard,
                ...(selected ? styles.tagCardActive : {}),
              }}
            >
              <div style={styles.tagIcon}>{opt.icon}</div>
              <div style={styles.tagTextCol}>
                <div style={styles.tagTitle}>{t(opt.titleKey)}</div>
                <div style={styles.tagDesc}>{t(opt.descKey)}</div>
              </div>
              <div
                style={{
                  ...styles.tagCheck,
                  background: selected ? teal : 'transparent',
                  borderColor: selected ? teal : '#E5E7EB',
                }}
              >
                {selected && <span style={styles.tagCheckMark}>✓</span>}
              </div>
            </button>
          )
        })}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.actionsRow}>
        <button style={styles.btnSecondary} onClick={onBack} disabled={saving}>
          {t('common.back')}
        </button>
        <button
          style={{
            ...styles.btnPrimary,
            opacity: (saving || selectedMask === 0) ? 0.5 : 1,
            cursor: (saving || selectedMask === 0) ? 'not-allowed' : 'pointer',
          }}
          onClick={onContinue}
          disabled={saving || selectedMask === 0}
        >
          {saving ? t('common.saving') : t('common.continue')}
        </button>
      </div>

      <p style={styles.footnote}>{t('onboarding.tags_footnote')}</p>
    </div>
  )
}

// ---------- Step 3: Welcome / next steps ----------
function WelcomeStep({ t, onFinish, saving, error }) {
  return (
    <div style={styles.step}>
      <div style={styles.stepIcon}>🎉</div>
      <h1 style={styles.h1}>{t('onboarding.welcome_title')}</h1>
      <p style={styles.sub}>{t('onboarding.welcome_sub')}</p>

      <div style={styles.nextList}>
        <NextStep icon="🚗" title={t('onboarding.next_vehicle')} />
        <NextStep icon="💳" title={t('onboarding.next_payment')} />
        <NextStep icon="📸" title={t('onboarding.next_receipt')} />
        <NextStep icon="🗺️" title={t('onboarding.next_trip')} />
        <NextStep icon="📄" title={t('onboarding.next_report')} />
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <button
        style={{
          ...styles.btnPrimary,
          ...styles.btnFullWidth,
          opacity: saving ? 0.5 : 1,
        }}
        onClick={onFinish}
        disabled={saving}
      >
        {saving ? t('common.saving') : t('onboarding.welcome_cta')}
      </button>

      <p style={styles.footnote}>{t('onboarding.welcome_footnote')}</p>
    </div>
  )
}

function NextStep({ icon, title }) {
  return (
    <div style={styles.nextStep}>
      <span style={styles.nextStepIcon}>{icon}</span>
      <span style={styles.nextStepTitle}>{title}</span>
    </div>
  )
}

// ---------- Styles ----------
const styles = {
  root: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: cream,
    zIndex: 5000,
    overflowY: 'auto',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  container: {
    maxWidth: '520px',
    margin: '0 auto',
    padding: '32px 24px 60px',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  logoBar: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoText: {
    fontFamily: "'Fraunces', serif",
    fontSize: '22px',
    fontWeight: 900,
    color: plum,
    letterSpacing: '-0.5px',
  },
  logoLog: {
    color: teal,
    fontStyle: 'italic',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    marginBottom: '36px',
  },
  dot: {
    height: '8px',
    borderRadius: '100px',
    transition: 'all 0.2s ease',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  stepIcon: {
    fontSize: '48px',
    textAlign: 'center',
    marginBottom: '16px',
  },
  h1: {
    fontFamily: "'Fraunces', serif",
    fontSize: '30px',
    fontWeight: 900,
    color: plum,
    textAlign: 'center',
    letterSpacing: '-0.6px',
    lineHeight: 1.15,
    margin: '0 0 12px 0',
  },
  sub: {
    fontSize: '15px',
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 1.55,
    margin: '0 0 32px 0',
    fontWeight: 500,
  },

  // Language step
  langGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '24px',
  },
  langBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px 18px',
    background: '#fff',
    border: '1.5px solid #E5E7EB',
    borderRadius: '14px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'all 0.15s ease',
  },
  langFlag: {
    fontSize: '28px',
    lineHeight: 1,
  },
  langTextCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  langLabel: {
    fontSize: '15px',
    fontWeight: 700,
    color: plum,
  },
  langSub: {
    fontSize: '12px',
    color: '#9CA3AF',
    fontWeight: 500,
  },
  langChevron: {
    color: teal,
    fontSize: '18px',
    fontWeight: 700,
  },

  // Tag types step
  tagGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
  },
  tagCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    padding: '16px 18px',
    background: '#fff',
    border: '1.5px solid #E5E7EB',
    borderRadius: '14px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'all 0.15s ease',
    width: '100%',
  },
  tagCardActive: {
    borderColor: teal,
    background: '#E6FFFA',
    boxShadow: `0 0 0 3px ${teal}20`,
  },
  tagIcon: {
    fontSize: '28px',
    lineHeight: 1,
  },
  tagTextCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  tagTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: plum,
    lineHeight: 1.3,
  },
  tagDesc: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: 500,
    lineHeight: 1.45,
  },
  tagCheck: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    border: '1.5px solid #E5E7EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s ease',
    marginTop: '3px',
  },
  tagCheckMark: {
    color: '#fff',
    fontSize: '13px',
    fontWeight: 900,
  },

  // Welcome step
  nextList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '24px',
    padding: '18px',
    background: '#fff',
    borderRadius: '14px',
    border: '1.5px solid #E5E7EB',
  },
  nextStep: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '6px 0',
  },
  nextStepIcon: {
    fontSize: '22px',
  },
  nextStepTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: plum,
    flex: 1,
  },

  // Shared
  error: {
    background: '#FEE2E2',
    color: '#991B1B',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 500,
    marginBottom: '16px',
    textAlign: 'center',
  },
  actionsRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px',
  },
  btnPrimary: {
    flex: 2,
    padding: '14px',
    background: teal,
    color: '#042f2e',
    border: 'none',
    borderRadius: '100px',
    fontSize: '15px',
    fontWeight: 800,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 6px 20px ${teal}40`,
    transition: 'all 0.15s ease',
  },
  btnSecondary: {
    flex: 1,
    padding: '14px',
    background: '#fff',
    color: plum,
    border: '1.5px solid #E5E7EB',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnFullWidth: {
    width: '100%',
    flex: 'none',
    marginBottom: '12px',
  },
  footnote: {
    fontSize: '12px',
    color: '#9CA3AF',
    textAlign: 'center',
    lineHeight: 1.5,
    margin: '8px 0 0',
    fontWeight: 500,
  },
}
